import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  createCustomQuestionnaireDraft,
} from '@/lib/api/questionnaires';
import type { QuestionnaireCardData } from '@/components/questionnaires/final-page/types';
import { showErrorToast } from '@/utils/toast-utils';

const ADDITIONAL_QUESTIONS_HASH = '#additional-questions';

export function useQuestionnaireReviewLauncher() {
  const navigate = useNavigate();

  const launchMutation = useMutation({
    mutationFn: async (card: QuestionnaireCardData) => {
      if (card.type === 'core') {
        const created = await createCustomQuestionnaireDraft({
          sourceQuestionnaireId: card.id,
          name: card.title,
        });
        return created.questionnaireId;
      }

      return card.id;
    },
    onSuccess: (targetQuestionnaireId) => {
      navigate(`/questionnaires/${targetQuestionnaireId}${ADDITIONAL_QUESTIONS_HASH}`);
    },
    onError: (error) => {
      showErrorToast(error, 'Unable to open questionnaire editor');
    },
  });

  const openAdditionalQuestions = React.useCallback(
    (
      card: QuestionnaireCardData,
      options?: { forceCreateDraft?: boolean }
    ) => {
      if (launchMutation.isPending && launchMutation.variables?.id === card.id) {
        return;
      }

      if (options?.forceCreateDraft && card.type !== 'core') {
        launchMutation.mutate({
          ...card,
          type: 'core',
        });
        return;
      }

      launchMutation.mutate(card);
    },
    [launchMutation]
  );

  return {
    openAdditionalQuestions,
    pendingQuestionnaireId: launchMutation.isPending ? launchMutation.variables?.id : undefined,
  };
}
